import { useEffect, useState } from "react";
import {
  FolderKanban,
  FileText,
  Users,
  TrendingUp,
  CheckCircle2,
  Clock3,
  Pencil,
  Clock,
  Hash,
  ListTodo,
  Loader2,
} from "lucide-react";

import { getWorkspaceItems } from "../services/workspaceService";

const InfoCard = ({ icon: Icon, title, value }) => {
  return (
    <div className="rounded-lg bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon size={17} />

        <span className="text-xs font-medium uppercase">
          {title}
        </span>
      </div>

      <h3 className="mt-3 text-lg font-semibold text-slate-800">
        {value ?? "Not available"}
      </h3>
    </div>
  );
};

const WorkspaceOverview = ({
  workspace,
  onEdit,
}) => {
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState("");

  /*
   * ==========================================================
   * WORKSPACE UUID
   * ==========================================================
   */

  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  /*
   * ==========================================================
   * FETCH WORKSPACE ITEMS
   * ==========================================================
   */

  useEffect(() => {
    const fetchItems = async () => {
      if (!workspaceUuid) return;

      try {
        setLoadingItems(true);
        setItemsError("");

        const response =
          await getWorkspaceItems(workspaceUuid);

        console.log(
          "WORKSPACE ITEMS FOR OVERVIEW:",
          response
        );

        const itemList =
          Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        setItems(itemList);
      } catch (error) {
        console.error(
          "FETCH WORKSPACE ITEMS ERROR:",
          error
        );

        setItemsError(
          error?.response?.data?.message ||
            "Failed to load workspace tasks."
        );

        setItems([]);
      } finally {
        setLoadingItems(false);
      }
    };

    fetchItems();
  }, [workspaceUuid]);

  /*
   * ==========================================================
   * CONDITIONAL RETURN
   * ==========================================================
   */

  if (!workspace) return null;

  /*
   * ==========================================================
   * WORKSPACE DATA
   * ==========================================================
   */

  const workspaceName =
    workspace.workspace_name ||
    workspace.name ||
    "Unnamed Workspace";

  const workspaceCode =
    workspace.workspace_code || "-";

  const description =
    workspace.description ||
    "No description available.";

  const executionMode =
    workspace.execution_mode || "-";

  const estimatedHours =
    workspace.estimated_hours ?? 0;

  const status =
    workspace.status || "Planning";

  const displayOrder =
    workspace.display_order ?? "-";

  /*
   * ==========================================================
   * TASK CALCULATIONS
   * ==========================================================
   */

  const totalTasks = items.length;

  const completedTasks = items.filter(
    (item) =>
      String(item.status || "").toLowerCase() ===
      "completed"
  ).length;

  const remainingTasks =
    totalTasks - completedTasks;

  const progress =
    totalTasks > 0
      ? Math.round(
          (completedTasks / totalTasks) * 100
        )
      : 0;

  /*
   * ==========================================================
   * MEMBERS
   * ==========================================================
   */

  const members = Number(
    workspace.members ?? 0
  );

  return (
    <div className="space-y-6">

      {/* ================= HEADER ================= */}

      <div className="flex items-center justify-between">

        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Workspace Overview
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Overview and configuration of this workspace.
          </p>
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          <Pencil size={16} />
          Edit Workspace
        </button>

      </div>

      {/* ================= STATISTICS ================= */}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">

        <InfoCard
          icon={CheckCircle2}
          title="Status"
          value={status}
        />

        <InfoCard
          icon={Clock}
          title="Estimated Hours"
          value={`${estimatedHours} hrs`}
        />

        <InfoCard
          icon={TrendingUp}
          title="Execution Mode"
          value={executionMode}
        />

        <InfoCard
          icon={Hash}
          title="Workspace Code"
          value={workspaceCode}
        />

      </div>

      {/* ================= WORKSPACE INFORMATION ================= */}

      {/* <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <h2 className="mb-6 text-xl font-bold text-slate-800">
          Workspace Information
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

          <InfoCard
            icon={FolderKanban}
            title="Workspace"
            value={workspaceName}
          />

          <InfoCard
            icon={FileText}
            title="Description"
            value={description}
          />

          <InfoCard
            icon={TrendingUp}
            title="Execution Mode"
            value={executionMode}
          />

          <InfoCard
            icon={Clock}
            title="Estimated Hours"
            value={`${estimatedHours} hrs`}
          />

          <InfoCard
            icon={Hash}
            title="Workspace Code"
            value={workspaceCode}
          />

          <InfoCard
            icon={FolderKanban}
            title="Display Order"
            value={displayOrder}
          />

        </div>

      </div> */}

      {/* ================= PROGRESS ================= */}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <div className="flex items-center justify-between">

          <div>
            <h2 className="text-xl font-bold text-slate-800">
              Workspace Progress
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Progress based on completed workspace tasks
            </p>
          </div>

          {loadingItems ? (
            <Loader2
              size={25}
              className="animate-spin text-blue-600"
            />
          ) : (
            <h2 className="text-3xl font-bold text-blue-600">
              {progress}%
            </h2>
          )}

        </div>

        {/* Progress Bar */}

        <div className="mt-6 h-3 rounded-full bg-slate-200">

          <div
            className="h-3 rounded-full bg-blue-600 transition-all duration-500"
            style={{
              width: `${progress}%`,
            }}
          />

        </div>

        {/* Task Information */}

        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">

          <span>
            {completedTasks} completed
          </span>

          <span>
            {remainingTasks} remaining
          </span>

          <span>
            {totalTasks} total tasks
          </span>

        </div>

        {itemsError && (
          <p className="mt-3 text-sm text-red-600">
            {itemsError}
          </p>
        )}

      </div>

      {/* ================= SUMMARY ================= */}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <h2 className="mb-6 text-xl font-bold text-slate-800">
          Workspace Summary
        </h2>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">

          {/* Completed */}

          <div className="rounded-lg bg-green-50 p-5">

            <CheckCircle2
              className="text-green-600"
              size={22}
            />

            <h3 className="mt-3 text-2xl font-bold text-green-700">
              {completedTasks}
            </h3>

            <p className="text-sm text-slate-600">
              Completed Tasks
            </p>

          </div>

          {/* Remaining */}

          <div className="rounded-lg bg-yellow-50 p-5">

            <Clock3
              className="text-yellow-600"
              size={22}
            />

            <h3 className="mt-3 text-2xl font-bold text-yellow-700">
              {remainingTasks}
            </h3>

            <p className="text-sm text-slate-600">
              Remaining Tasks
            </p>

          </div>

          {/* Total */}

          <div className="rounded-lg bg-blue-50 p-5">

            <ListTodo
              className="text-blue-600"
              size={22}
            />

            <h3 className="mt-3 text-2xl font-bold text-blue-700">
              {totalTasks}
            </h3>

            <p className="text-sm text-slate-600">
              Total Tasks
            </p>

          </div>

        </div>

      </div>

    </div>
  );
};

export default WorkspaceOverview;